"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, CheckCircle, ArrowRight, Link2 } from "lucide-react";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import { HomeCtaSection } from "@/features/home/presentation/components/home-cta-section";
import type { BlogPost } from "../../types/blog";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { useEffect, useMemo, useRef, useState } from "react";
import type { SiteFooterContent, SiteNavbarContent } from "@/shared/types/site-shell-content";
import type { HomeCtaSectionContent } from "@/features/home/types/home-page-content";
interface BlogDetailPageProps {
  blog: BlogPost;
  relatedBlogs: BlogPost[];
  navigation: SiteNavbarContent;
  footer: SiteFooterContent;
  ctaSection: HomeCtaSectionContent;
  locale: AppLocale;
}
function slugifyHeading(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9ऀ-ॿ]+/g, "-").replace(/(^-|-$)/g, "");
}
function LinkedInIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function XIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.74l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function WhatsAppIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ShareButtons(t0) {
  const $ = _c(33);
  const {
    blog,
    locale
  } = t0;
  const [copied, setCopied] = useState(false);
  const getUrl = _temp;
  let t1;
  if ($[0] !== blog.title) {
    t1 = platform => {
      const url = getUrl();
      const title = blog.title;
      if (platform === "linkedin") {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank");
      } else {
        if (platform === "twitter") {
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, "_blank");
        } else {
          if (platform === "whatsapp") {
            window.open(`https://wa.me/?text=${encodeURIComponent(title + " " + url)}`, "_blank");
          } else {
            if (platform === "copy") {
              navigator.clipboard.writeText(url).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }
          }
        }
      }
    };
    $[0] = blog.title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleShare = t1;
  const t2 = locale === "hi" ? "\u0936\u0947\u092F\u0930 \u0915\u0930\u0947\u0902" : "Share this post";
  let t3;
  if ($[2] !== t2) {
    t3 = <span className="text-sm font-medium text-muted-foreground">{t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== handleShare) {
    t4 = () => handleShare("linkedin");
    $[4] = handleShare;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <LinkedInIcon />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <button onClick={t4} className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-primary hover:bg-border/60 transition-colors duration-200" title="Share on LinkedIn">{t5}</button>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== handleShare) {
    t7 = () => handleShare("twitter");
    $[9] = handleShare;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <XIcon />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7) {
    t9 = <button onClick={t7} className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-primary hover:bg-border/60 transition-colors duration-200" title="Share on X">{t8}</button>;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== handleShare) {
    t10 = () => handleShare("whatsapp");
    $[14] = handleShare;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <WhatsAppIcon />;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10) {
    t12 = <button onClick={t10} className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-primary hover:bg-border/60 transition-colors duration-200" title="Share on WhatsApp">{t11}</button>;
    $[17] = t10;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== handleShare) {
    t13 = () => handleShare("copy");
    $[19] = handleShare;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = copied ? "Copied!" : "Copy link";
  const t15 = `h-4 w-4 ${copied ? "text-primary" : ""}`;
  let t16;
  if ($[21] !== t15) {
    t16 = <Link2 className={t15} />;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t13 || $[24] !== t14 || $[25] !== t16) {
    t17 = <button onClick={t13} className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-primary hover:bg-border/60 transition-colors duration-200" title={t14}>{t16}</button>;
    $[23] = t13;
    $[24] = t14;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t12 || $[28] !== t17 || $[29] !== t3 || $[30] !== t6 || $[31] !== t9) {
    t18 = <div className="flex items-center gap-2.5">{t3}{t6}{t9}{t12}{t17}</div>;
    $[27] = t12;
    $[28] = t17;
    $[29] = t3;
    $[30] = t6;
    $[31] = t9;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  return t18;
}
function _temp() {
  return typeof window !== "undefined" ? window.location.href : "";
}
function TableOfContents(t0) {
  const $ = _c(10);
  const {
    items,
    activeId,
    locale
  } = t0;
  if (items.length === 0) {
    return null;
  }
  const t1 = locale === "hi" ? "\u0935\u093F\u0937\u092F-\u0938\u0942\u091A\u0940" : "Table of content";
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="mb-4 text-[11px] font-bold tracking-widest text-muted-foreground uppercase">{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== activeId || $[3] !== items) {
    let t4;
    if ($[5] !== activeId) {
      t4 = item => <a key={item.id} href={`#${item.id}`} onClick={e => {
        e.preventDefault();
        const el = document.getElementById(item.id);
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }} className={`block border-l-2 py-1.5 pl-3 text-sm leading-snug transition-all duration-200 ${activeId === item.id ? "border-primary font-semibold text-primary" : "border-transparent font-normal text-muted-foreground hover:border-border hover:text-foreground"}`}>{item.text}</a>;
      $[5] = activeId;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = items.map(t4);
    $[2] = activeId;
    $[3] = items;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <nav className="space-y-1">{t2}{t3}</nav>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function BlogDetailPage(t0) {
  const $ = _c(144);
  const {
    blog,
    relatedBlogs,
    navigation,
    footer,
    ctaSection,
    locale
  } = t0;
  const {
    scrollYProgress
  } = useScroll();
  let t1;
  if ($[0] !== blog.content) {
    t1 = blog.content.filter(_temp2).map(_temp3);
    $[0] = blog.content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tocItems = t1;
  const [activeId, setActiveId] = useState(tocItems[0]?.id ?? "");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const headingRefs = useRef(t2);
  let t3;
  let t4;
  if ($[3] !== tocItems) {
    t3 = () => {
      if (tocItems.length === 0) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        const visible = entries.filter(_temp4).sort(_temp5);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      }, {
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0
      });
      tocItems.forEach(t5 => {
        const {
          id
        } = t5;
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
        }
      });
      return () => observer.disconnect();
    };
    t4 = [tocItems];
    $[3] = tocItems;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== scrollYProgress) {
    t5 = <motion.div className="fixed top-0 right-0 left-0 z-[110] h-0.5 origin-left bg-primary" style={{
      scaleX: scrollYProgress
    }} />;
    $[6] = scrollYProgress;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== navigation) {
    t6 = <SiteNavbar content={navigation} />;
    $[8] = navigation;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 12
    };
    t8 = {
      opacity: 1,
      y: 0
    };
    t9 = {
      duration: 0.5
    };
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[13] !== locale) {
    t10 = localizeHref(locale, "/blogs");
    $[13] = locale;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = locale === "hi" ? "\u0935\u093E\u092A\u0938 \u091C\u0930\u094D\u0928\u0932 \u092E\u0947\u0902" : "Back to Journal";
  let t13;
  if ($[16] !== t10 || $[17] !== t12) {
    t13 = <motion.div initial={t7} animate={t8} transition={t9} className="mb-8"><Link href={t10} className="group inline-flex items-center gap-2 text-xs font-semibold text-primary transition-colors hover:text-foreground">{t11}{t12}</Link></motion.div>;
    $[16] = t10;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: 0,
      scale: 0.97,
      y: 20
    };
    t15 = {
      opacity: 1,
      scale: 1,
      y: 0
    };
    $[19] = t14;
    $[20] = t15;
  } else {
    t14 = $[19];
    t15 = $[20];
  }
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1]
    };
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== blog.imageUrl || $[23] !== blog.title) {
    t17 = <motion.div initial={t14} animate={t15} transition={t16} className="relative aspect-[4/3] w-full overflow-hidden rounded-[2rem] border border-border bg-muted shadow-sm"><Image src={blog.imageUrl} alt={blog.title} fill={true} className="object-cover" priority={true} sizes="(max-width: 1024px) 100vw, 50vw" /></motion.div>;
    $[22] = blog.imageUrl;
    $[23] = blog.title;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  let t19;
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      opacity: 0,
      y: 24
    };
    t19 = {
      opacity: 1,
      y: 0
    };
    t20 = {
      duration: 0.8,
      delay: 0.15
    };
    $[25] = t18;
    $[26] = t19;
    $[27] = t20;
  } else {
    t18 = $[25];
    t19 = $[26];
    t20 = $[27];
  }
  let t21;
  if ($[28] !== blog.category) {
    t21 = <span className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-semibold tracking-wide text-primary">{blog.category}</span>;
    $[28] = blog.category;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== blog.tags) {
    t22 = blog.tags.map(_temp6);
    $[30] = blog.tags;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== blog.date) {
    t23 = <span className="ml-auto shrink-0 text-[11px] font-medium text-muted-foreground">{blog.date}</span>;
    $[32] = blog.date;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== t21 || $[35] !== t22 || $[36] !== t23) {
    t24 = <div className="flex flex-wrap items-center gap-2">{t21}{t22}{t23}</div>;
    $[34] = t21;
    $[35] = t22;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== blog.title) {
    t25 = <h1 className="font-heading text-3xl leading-[1.15] font-light tracking-tight text-foreground sm:text-4xl xl:text-[2.75rem]">{blog.title}</h1>;
    $[38] = blog.title;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== blog.excerpt) {
    t26 = <p className="max-w-xl text-[1rem] leading-relaxed font-light text-muted-foreground">{blog.excerpt}</p>;
    $[40] = blog.excerpt;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== blog || $[43] !== locale) {
    t27 = <ShareButtons blog={blog} locale={locale} />;
    $[42] = blog;
    $[43] = locale;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== t24 || $[46] !== t25 || $[47] !== t26 || $[48] !== t27) {
    t28 = <motion.div initial={t18} animate={t19} transition={t20} className="flex flex-col gap-5">{t24}{t25}{t26}{t27}</motion.div>;
    $[45] = t24;
    $[46] = t25;
    $[47] = t26;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t17 || $[51] !== t28) {
    t29 = <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">{t17}{t28}</div>;
    $[50] = t17;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t13 || $[54] !== t29) {
    t30 = <section className="px-5 pt-24 pb-0 sm:px-10 sm:pt-32 lg:px-16"><div className="mx-auto max-w-7xl">{t13}{t29}</div></section>;
    $[53] = t13;
    $[54] = t29;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  let t32;
  let t33;
  let t34;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      opacity: 0,
      y: 30
    };
    t32 = {
      opacity: 1,
      y: 0
    };
    t33 = {
      once: true,
      margin: "-80px"
    };
    t34 = {
      duration: 0.8
    };
    $[56] = t31;
    $[57] = t32;
    $[58] = t33;
    $[59] = t34;
  } else {
    t31 = $[56];
    t32 = $[57];
    t33 = $[58];
    t34 = $[59];
  }
  let t35;
  if ($[60] !== activeId || $[61] !== locale || $[62] !== tocItems) {
    t35 = tocItems.length > 0 && <aside className="sticky top-28 hidden self-start lg:block"><div><TableOfContents items={tocItems} activeId={activeId} locale={locale} /></div></aside>;
    $[60] = activeId;
    $[61] = locale;
    $[62] = tocItems;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== blog.content) {
    t36 = blog.content.map((block_1, idx) => {
      if (block_1.type === "paragraph") {
        return <p key={idx} className="mb-7 font-sans text-[1.0625rem] leading-[1.85] font-light text-foreground sm:text-[1.125rem]">{block_1.text}</p>;
      }
      if (block_1.type === "heading") {
        const id_0 = slugifyHeading(block_1.text);
        return <h2 key={idx} id={id_0} ref={el_0 => {
          headingRefs.current[id_0] = el_0;
        }} className="mt-14 mb-5 scroll-mt-32 font-heading text-2xl leading-tight font-semibold tracking-tight text-foreground sm:text-[1.75rem]">{block_1.text}</h2>;
      }
      if (block_1.type === "pullquote") {
        return <blockquote key={idx} className="my-10 border-l-[3px] border-primary pl-6 text-[1.2rem] leading-relaxed font-light text-foreground italic sm:text-[1.3rem]">{block_1.text}</blockquote>;
      }
      if (block_1.type === "list" && block_1.items) {
        return <ul key={idx} className="my-7 space-y-3 pl-2">{block_1.items.map(_temp7)}</ul>;
      }
      if (block_1.type === "image" && block_1.url) {
        return <figure key={idx} className="my-8"><div className="relative w-full overflow-hidden rounded-2xl border border-border bg-muted"><img src={block_1.url} alt={block_1.altText ?? ""} className="w-full object-cover" /></div>{block_1.caption && <figcaption className="mt-2 text-center text-sm font-light text-muted-foreground">{block_1.caption}</figcaption>}</figure>;
      }
      return null;
    });
    $[64] = blog.content;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  let t37;
  if ($[66] !== t36) {
    t37 = <div className="prose prose-slate max-w-none">{t36}</div>;
    $[66] = t36;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  let t38;
  if ($[68] !== blog || $[69] !== locale) {
    t38 = <div className="mt-16 border-t border-border pt-8"><ShareButtons blog={blog} locale={locale} /></div>;
    $[68] = blog;
    $[69] = locale;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== blog.author.avatarUrl || $[72] !== blog.author.name) {
    t39 = <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-border"><Image src={blog.author.avatarUrl} alt={blog.author.name} fill={true} className="object-cover" /></div>;
    $[71] = blog.author.avatarUrl;
    $[72] = blog.author.name;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  const t40 = locale === "hi" ? "\u0932\u0947\u0916\u0915 / \u0925\u0947\u0930\u0947\u092A\u093F\u0938\u094D\u091F" : "Author / Therapist";
  let t41;
  if ($[74] !== t40) {
    t41 = <p className="mb-1 text-[11px] font-semibold tracking-widest text-primary uppercase">{t40}</p>;
    $[74] = t40;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  let t42;
  if ($[76] !== blog.author.name) {
    t42 = <h4 className="font-heading text-xl font-semibold text-foreground">{blog.author.name}</h4>;
    $[76] = blog.author.name;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  let t43;
  if ($[78] !== blog.author.isVerified) {
    t43 = blog.author.isVerified && <CheckCircle className="h-4 w-4 shrink-0 fill-primary text-background" />;
    $[78] = blog.author.isVerified;
    $[79] = t43;
  } else {
    t43 = $[79];
  }
  let t44;
  if ($[80] !== t42 || $[81] !== t43) {
    t44 = <div className="flex items-center gap-1.5">{t42}{t43}</div>;
    $[80] = t42;
    $[81] = t43;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== blog.author.specialization) {
    t45 = <p className="mt-0.5 text-sm font-light text-muted-foreground">{blog.author.specialization}</p>;
    $[83] = blog.author.specialization;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== blog.author.bio) {
    t46 = blog.author.bio && <p className="mt-2 line-clamp-2 text-sm leading-relaxed font-light text-muted-foreground">{blog.author.bio}</p>;
    $[85] = blog.author.bio;
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  let t47;
  if ($[87] !== t41 || $[88] !== t44 || $[89] !== t45 || $[90] !== t46) {
    t47 = <div className="min-w-0 flex-1">{t41}{t44}{t45}{t46}</div>;
    $[87] = t41;
    $[88] = t44;
    $[89] = t45;
    $[90] = t46;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  let t48;
  if ($[92] !== locale) {
    t48 = localizeHref(locale, "/healers");
    $[92] = locale;
    $[93] = t48;
  } else {
    t48 = $[93];
  }
  let t49;
  if ($[94] !== blog.author.name || $[95] !== locale) {
    t49 = locale === "hi" ? "\u092A\u094D\u0930\u094B\u092B\u093C\u093E\u0907\u0932 \u0926\u0947\u0916\u0947\u0902" : `View ${blog.author.name.split(" ")[blog.author.name.split(" ").length > 1 ? 1 : 0]}'s profile`;
    $[94] = blog.author.name;
    $[95] = locale;
    $[96] = t49;
  } else {
    t49 = $[96];
  }
  let t50;
  if ($[97] !== t48 || $[98] !== t49) {
    t50 = <Link href={t48} className="inline-flex shrink-0 items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-semibold whitespace-nowrap text-white transition-colors duration-300 hover:bg-primary/90">{t49}</Link>;
    $[97] = t48;
    $[98] = t49;
    $[99] = t50;
  } else {
    t50 = $[99];
  }
  let t51;
  if ($[100] !== t39 || $[101] !== t47 || $[102] !== t50) {
    t51 = <div className="mt-8 border-t border-border pt-8"><div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">{t39}{t47}{t50}</div></div>;
    $[100] = t39;
    $[101] = t47;
    $[102] = t50;
    $[103] = t51;
  } else {
    t51 = $[103];
  }
  let t52;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(125,226,248,0.15),transparent_60%)]" />;
    $[104] = t52;
  } else {
    t52 = $[104];
  }
  const t53 = locale === "hi" ? "\u0938\u093E\u0925 \u092E\u093F\u0932\u0915\u0930 \u0939\u0940\u0932\u093F\u0902\u0917" : "Healing in Connection";
  let t54;
  if ($[105] !== t53) {
    t54 = <span className="text-[10px] font-bold tracking-[0.25em] text-secondary uppercase">{t53}</span>;
    $[105] = t53;
    $[106] = t54;
  } else {
    t54 = $[106];
  }
  const t55 = locale === "hi" ? "\u0915\u094D\u092F\u093E \u0906\u092A \u0907\u0928 \u0905\u0928\u0941\u092D\u0935\u094B\u0902 \u0915\u094B \u0905\u0915\u0947\u0932\u0947 \u092E\u0939\u0938\u0942\u0938 \u0915\u0930 \u0930\u0939\u0947 \u0939\u0948\u0902?" : "Are you walking this path alone?";
  let t56;
  if ($[107] !== t55) {
    t56 = <h3 className="mt-3 mb-3 font-heading text-2xl leading-tight font-light tracking-tight sm:text-3xl">{t55}</h3>;
    $[107] = t55;
    $[108] = t56;
  } else {
    t56 = $[108];
  }
  const t57 = locale === "hi" ? "ItsHealer \u092A\u0930 \u0939\u092E\u093E\u0930\u0947 \u092A\u094D\u0930\u092E\u093E\u0923\u093F\u0924 \u0939\u0940\u0932\u0930 \u0914\u0930 \u0925\u0947\u0930\u0947\u092A\u093F\u0938\u094D\u091F \u0906\u092A\u0915\u0947 \u092D\u093E\u0935\u0928\u093E\u0924\u094D\u092E\u0915 \u0926\u0930\u094D\u0926, \u090F\u0902\u0917\u094D\u091C\u093E\u092F\u091F\u0940 \u092F\u093E \u092C\u0930\u094D\u0928\u0906\u0909\u091F \u0915\u094B \u0938\u092E\u091D\u0928\u0947 \u0914\u0930 \u090F\u0915 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0938\u094D\u092A\u0947\u0938 \u092C\u0928\u093E\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0909\u092A\u0932\u092C\u094D\u0927 \u0939\u0948\u0902\u0964" : "We match you with licensed, compassionate clinical therapists who understand trauma, anxiety, and boundary setting. Start a secure, gentle check-in today.";
  let t58;
  if ($[109] !== t57) {
    t58 = <p className="mb-7 max-w-lg text-sm leading-relaxed font-light text-white/80 sm:text-base">{t57}</p>;
    $[109] = t57;
    $[110] = t58;
  } else {
    t58 = $[110];
  }
  let t59;
  if ($[111] !== locale) {
    t59 = localizeHref(locale, "/signup/seeker");
    $[111] = locale;
    $[112] = t59;
  } else {
    t59 = $[112];
  }
  const t60 = locale === "hi" ? "\u0905\u092A\u0928\u0940 \u0939\u0940\u0932\u093F\u0902\u0917 \u0936\u0941\u0930\u0942 \u0915\u0930\u0947\u0902" : "Begin Healing Journey";
  let t61;
  if ($[113] !== t59 || $[114] !== t60) {
    t61 = <Link href={t59} className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-sm font-semibold text-primary transition-colors duration-300 hover:bg-secondary hover:text-foreground">{t60}</Link>;
    $[113] = t59;
    $[114] = t60;
    $[115] = t61;
  } else {
    t61 = $[115];
  }
  let t62;
  if ($[116] !== t54 || $[117] !== t56 || $[118] !== t58 || $[119] !== t61) {
    t62 = <div className="relative mt-14 overflow-hidden rounded-[2rem] border border-border bg-primary p-8 text-white shadow-lg sm:p-10">{t52}<div className="relative z-10">{t54}{t56}{t58}{t61}</div></div>;
    $[116] = t54;
    $[117] = t56;
    $[118] = t58;
    $[119] = t61;
    $[120] = t62;
  } else {
    t62 = $[120];
  }
  let t63;
  if ($[121] !== t37 || $[122] !== t38 || $[123] !== t51 || $[124] !== t62) {
    t63 = <div className="min-w-0">{t37}{t38}{t51}{t62}</div>;
    $[121] = t37;
    $[122] = t38;
    $[123] = t51;
    $[124] = t62;
    $[125] = t63;
  } else {
    t63 = $[125];
  }
  let t64;
  if ($[126] !== t35 || $[127] !== t63) {
    t64 = <motion.section initial={t31} whileInView={t32} viewport={t33} transition={t34} className="mx-auto max-w-7xl px-5 pt-16 pb-20 sm:px-10 lg:px-16"><div className="grid grid-cols-1 gap-14 lg:grid-cols-[280px_1fr]">{t35}{t63}</div></motion.section>;
    $[126] = t35;
    $[127] = t63;
    $[128] = t64;
  } else {
    t64 = $[128];
  }
  let t65;
  if ($[129] !== locale || $[130] !== relatedBlogs) {
    t65 = relatedBlogs.length > 0 && <section className="border-t border-border bg-muted/50 px-5 py-20 sm:px-10 lg:px-16"><div className="mx-auto max-w-7xl"><motion.h3 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-10 border-b border-border pb-4 font-heading text-xl font-light tracking-tight text-foreground sm:text-2xl">{locale === "hi" ? "\u0938\u092E\u094D\u092C\u0902\u0927\u093F\u0924 \u0932\u0947\u0916" : "Further Gentle Reading"}</motion.h3><div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{relatedBlogs.map((item_0, index) => <motion.div key={item_0.slug} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: index * 0.1
          }} className="group flex flex-col overflow-hidden rounded-[1.75rem] border border-border bg-background shadow-sm transition-all duration-500 hover:shadow-md"><div className="relative aspect-[16/10] w-full overflow-hidden bg-muted"><Image src={item_0.imageUrl} alt={item_0.title} fill={true} className="object-cover grayscale-[15%] transition-all duration-700 ease-out group-hover:scale-[1.04] group-hover:grayscale-0" /></div><div className="flex flex-1 flex-col p-5 sm:p-6"><span className="text-[10px] font-bold tracking-wider text-primary uppercase">{item_0.category}</span><Link href={localizeHref(locale, `/blogs/${item_0.slug}`)}><h4 className="mt-2 line-clamp-2 font-heading text-lg leading-snug font-light text-foreground transition-colors duration-300 group-hover:text-primary">{item_0.title}</h4></Link><div className="mt-auto flex items-center justify-between border-t border-border pt-5 text-xs text-muted-foreground"><div className="flex items-center gap-1.5"><div className="relative h-5 w-5 overflow-hidden rounded-full border border-border"><Image src={item_0.author.avatarUrl} alt={item_0.author.name} fill={true} className="object-cover" /></div><span className="text-[10px] font-medium text-foreground">{item_0.author.name}</span>{item_0.author.isVerified && <CheckCircle className="h-3 w-3 shrink-0 fill-primary text-background" />}</div><Link href={localizeHref(locale, `/blogs/${item_0.slug}`)} className="inline-flex items-center gap-1 font-semibold text-primary transition-colors hover:text-foreground">{locale === "hi" ? "\u092A\u0922\u093C\u0947\u0902" : "Read"}<ArrowRight className="h-3 w-3" /></Link></div></div></motion.div>)}</div></div></section>;
    $[129] = locale;
    $[130] = relatedBlogs;
    $[131] = t65;
  } else {
    t65 = $[131];
  }
  let t66;
  if ($[132] !== ctaSection) {
    t66 = <HomeCtaSection content={ctaSection} />;
    $[132] = ctaSection;
    $[133] = t66;
  } else {
    t66 = $[133];
  }
  let t67;
  if ($[134] !== footer) {
    t67 = <SiteFooter content={footer} />;
    $[134] = footer;
    $[135] = t67;
  } else {
    t67 = $[135];
  }
  let t68;
  if ($[136] !== t30 || $[137] !== t5 || $[138] !== t6 || $[139] !== t64 || $[140] !== t65 || $[141] !== t66 || $[142] !== t67) {
    t68 = <div className="relative min-h-screen overflow-x-hidden bg-background font-sans text-foreground selection:bg-accent/20 selection:text-foreground">{t5}{t6}{t30}{t64}{t65}{t66}{t67}</div>;
    $[136] = t30;
    $[137] = t5;
    $[138] = t6;
    $[139] = t64;
    $[140] = t65;
    $[141] = t66;
    $[142] = t67;
    $[143] = t68;
  } else {
    t68 = $[143];
  }
  return t68;
}
function _temp7(item, itemIdx) {
  return <li key={itemIdx} className="flex gap-3 text-[1.0625rem] leading-relaxed font-light text-foreground"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" /><span>{item}</span></li>;
}
function _temp6(tag) {
  return <span key={tag} className="rounded-full border border-border bg-transparent px-3 py-1 text-[11px] font-medium text-muted-foreground">{tag}</span>;
}
function _temp5(a, b) {
  return a.boundingClientRect.top - b.boundingClientRect.top;
}
function _temp4(e) {
  return e.isIntersecting;
}
function _temp3(block_0) {
  return {
    text: block_0.text,
    id: slugifyHeading(block_0.text)
  };
}
function _temp2(block) {
  return block.type === "heading" && block.text;
}